import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct, monthLabel } from "@/lib/api";
import BulletHole from "@/components/BulletHole";
import { Printer, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const pad = (n) => String(n).padStart(2, "0");

export default function Dossier() {
  const [b, setB] = useState(null);
  const [d, setD] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/briefing").then((r) => r.data),
      api.get("/dashboard").then((r) => r.data),
    ]).then(([brief, dash]) => {
      setB(brief);
      setD(dash);
    });
    // hide body grain in print
  }, []);

  if (!b || !d) return null;

  const now = new Date();
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const fileNo = stamp.replace(/-/g, "");

  const handlePrint = () => window.print();

  return (
    <div className="dossier-wrap" data-testid="dossier">
      {/* Controls (hidden in print) */}
      <div className="print:hidden flex items-center justify-between mb-4 px-2">
        <Link to="/briefing" className="hud-btn flex items-center gap-2" data-testid="dossier-back">
          <ArrowLeft size={11} /> BACK TO BRIEFING
        </Link>
        <button
          onClick={handlePrint}
          className="hud-btn hud-btn-primary flex items-center gap-2"
          data-testid="dossier-print"
        >
          <Printer size={11} /> PRINT / SAVE AS PDF
        </button>
      </div>

      <article className="dossier-page mx-auto">
        {/* Header */}
        <header className="relative">
          <div className="tread-plate h-2 mb-5" />
          <BulletHole variant={2} size={90} className="absolute -top-2 right-2 opacity-60" />
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="dossier-label">Confidential</div>
              <div className="dossier-value text-[hsl(var(--oxblood))]">Confidential</div>
            </div>
            <div className="text-center">
              <div className="dossier-label">File</div>
              <div className="dossier-value mono">DOS-{fileNo}</div>
            </div>
            <div className="text-right">
              <div className="dossier-label">Issued</div>
              <div className="dossier-value mono">
                {now.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })}
              </div>
            </div>
          </div>
          <div className="border-y-2 border-foreground py-4">
            <div className="dossier-label tracking-[0.4em]">COMMAND CENTER · FOR CEO'S ONLY</div>
            <h1 className="dossier-title font-display font-bold uppercase mt-1 leading-none">
              Weekly Report
            </h1>
            <div className="mono uppercase text-xs tracking-[0.3em] mt-2 text-muted-foreground">
              {b.week_label}
            </div>
          </div>
        </header>

        {/* Verdict */}
        <section className="mt-7">
          <div className="dossier-label">Summary</div>
          <p className="font-display text-2xl font-medium mt-2 leading-snug">
            {b.verdict}
          </p>
        </section>

        {/* KPI row */}
        <section className="mt-6 grid grid-cols-4 gap-3">
          {[
            { l: "Habits 7-Day", v: fmtPct(b.habits.last7_pct) },
            { l: "Week Output", v: fmtPct(b.weekly.current_pct) },
            { l: "Pipeline Open", v: fmtMoney(b.deals.projected_open) },
            { l: "Media AR", v: fmtMoney(b.media.unpaid_value) },
          ].map((k) => (
            <div key={k.l} className="dossier-kpi">
              <div className="dossier-label">{k.l}</div>
              <div className="font-display text-3xl font-bold mt-1 tabular-nums leading-none">
                {k.v}
              </div>
            </div>
          ))}
        </section>

        {/* Habits + Week comparison */}
        <section className="mt-6 grid grid-cols-2 gap-6">
          <div>
            <div className="dossier-label">Top Performers · 7-Day</div>
            <table className="w-full mt-2 text-sm">
              <tbody>
                {b.habits.top.map((h, i) => (
                  <tr key={i} className="border-b border-foreground/20">
                    <td className="py-1.5">{h.name}</td>
                    <td className="py-1.5 text-right mono tabular-nums">
                      {h.completed}/7
                    </td>
                    <td className="py-1.5 text-right mono tabular-nums w-12">
                      {Math.round(h.pct * 100)}%
                    </td>
                  </tr>
                ))}
                {b.habits.top.length === 0 && (
                  <tr><td className="py-2 text-sm text-muted-foreground">No data.</td></tr>
                )}
              </tbody>
            </table>
            {b.habits.laggards.length > 0 && (
              <>
                <div className="dossier-label mt-5 text-[hsl(var(--oxblood))]">Laggards</div>
                <table className="w-full mt-2 text-sm">
                  <tbody>
                    {b.habits.laggards.map((h, i) => (
                      <tr key={i} className="border-b border-foreground/20">
                        <td className="py-1.5">{h.name}</td>
                        <td className="py-1.5 text-right mono tabular-nums">
                          {h.completed}/7
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          <div>
            <div className="dossier-label">Week Comparison</div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="dossier-kpi">
                <div className="dossier-label">This Week</div>
                <div className="font-display text-2xl font-bold mt-1 tabular-nums">
                  {fmtPct(b.weekly.current_pct)}
                </div>
              </div>
              <div className="dossier-kpi">
                <div className="dossier-label">Prior</div>
                <div className="font-display text-2xl font-bold mt-1 tabular-nums">
                  {fmtPct(b.weekly.previous_pct)}
                </div>
              </div>
            </div>
            <div className="mt-3 mono text-xs uppercase tracking-widest">
              Delta:{" "}
              <span
                className={`font-semibold ${
                  b.weekly.delta >= 0
                    ? "text-[hsl(var(--signal-cyan))]"
                    : "text-[hsl(var(--oxblood))]"
                }`}
              >
                {b.weekly.delta >= 0 ? "+" : ""}
                {Math.round(b.weekly.delta * 100)}%
              </span>{" "}
              vs prior week
            </div>

            <div className="dossier-label mt-5">Cycle</div>
            <div className="font-display text-xl font-semibold mt-1">
              {monthLabel(d.month)}
            </div>
            <div className="mono text-xs uppercase tracking-widest mt-1 text-muted-foreground">
              {d.closed_deals} closed · {d.active_deals} active deals · {d.media_orders} media orders
            </div>
          </div>
        </section>

        {/* Pipeline */}
        <section className="mt-6">
          <div className="dossier-label">Pipeline · Top Open Positions</div>
          {b.deals.top_open.length > 0 ? (
            <table className="w-full mt-2 text-sm">
              <thead>
                <tr className="border-b-2 border-foreground">
                  <th className="text-left py-1.5 mono uppercase text-[10px] tracking-widest">Client</th>
                  <th className="text-left py-1.5 mono uppercase text-[10px] tracking-widest">Property</th>
                  <th className="text-left py-1.5 mono uppercase text-[10px] tracking-widest">Stage</th>
                  <th className="text-left py-1.5 mono uppercase text-[10px] tracking-widest">Close</th>
                  <th className="text-right py-1.5 mono uppercase text-[10px] tracking-widest">Projected</th>
                </tr>
              </thead>
              <tbody>
                {b.deals.top_open.map((d, i) => (
                  <tr key={i} className="border-b border-foreground/20">
                    <td className="py-1.5">{d.client}</td>
                    <td className="py-1.5 text-muted-foreground">{d.property}</td>
                    <td className="py-1.5 mono text-xs uppercase">{d.stage}</td>
                    <td className="py-1.5 mono text-xs">{d.expected_close || "—"}</td>
                    <td className="py-1.5 text-right mono font-semibold">{fmtMoney(d.projected)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="mt-2 text-sm text-muted-foreground">No active deals on the board.</div>
          )}
        </section>

        {/* Media */}
        <section className="mt-6 grid grid-cols-2 gap-6">
          <div>
            <div className="dossier-label">Unpaid Ledger</div>
            {b.media.unpaid_list.length > 0 ? (
              <ul className="mt-2 space-y-1 text-sm">
                {b.media.unpaid_list.map((m, i) => (
                  <li key={i} className="flex items-baseline gap-2 border-b border-foreground/20 py-1">
                    <span className="flex-1 truncate">
                      {m.client}{" "}
                      <span className="text-muted-foreground">/ {m.property}</span>
                    </span>
                    <span className="mono font-semibold text-[hsl(var(--oxblood))]">
                      {fmtMoney(m.total)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 text-sm text-muted-foreground">All paid up.</div>
            )}
          </div>
          <div>
            <div className="dossier-label">Upcoming Shoots · Next 7d</div>
            {b.media.pending_shoots_next7.length > 0 ? (
              <ul className="mt-2 space-y-1 text-sm">
                {b.media.pending_shoots_next7.map((s, i) => (
                  <li key={i} className="flex items-baseline gap-2 border-b border-foreground/20 py-1">
                    <span className="mono text-xs w-24">{s.shoot_date} {s.shoot_time || ""}</span>
                    <span className="flex-1 truncate">{s.client} <span className="text-muted-foreground">/ {s.property}</span></span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 text-sm text-muted-foreground">No shoots scheduled.</div>
            )}
          </div>
        </section>

        {/* Followups */}
        {b.deals.followups_next7.length > 0 && (
          <section className="mt-6">
            <div className="dossier-label">Follow-Ups · Next 7d</div>
            <ul className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {b.deals.followups_next7.map((f, i) => (
                <li key={i} className="flex items-baseline gap-2 border-b border-foreground/20 py-1">
                  <span className="mono text-xs text-muted-foreground w-20">{f.date}</span>
                  <span className="flex-1 truncate">{f.client}</span>
                  <span className="mono text-xs uppercase">{f.priority}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Signature */}
        <footer className="mt-10 pt-4 border-t border-foreground">
          <div className="grid grid-cols-3 gap-6 items-end">
            <div>
              <div className="border-b border-foreground h-10" />
              <div className="dossier-label mt-1">Operator Signature</div>
            </div>
            <div className="text-center relative">
              <BulletHole variant={3} size={70} className="absolute left-1/2 -translate-x-1/2 -top-2 opacity-50" />
              <div className="font-display text-xs uppercase tracking-[0.4em] mt-6">Command<span className="text-[hsl(var(--oxblood))]">·</span>Center</div>
              <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-1">
                END OF DOSSIER · CONFIDENTIAL
              </div>
            </div>
            <div className="text-right">
              <div className="dossier-label">Issued by</div>
              <div className="font-display text-sm mt-1">COMMAND CENTER</div>
              <div className="mono text-[10px] text-muted-foreground mt-1">{new Date().toISOString().slice(0, 19).replace("T", " ")}Z</div>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
}
